import axios from 'axios';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import BlockUi from 'react-block-ui';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { useSubheader } from '../../../_metronic/layout/_core/MetronicSubheader';
import { validCnpj, validCpf } from '../../helpers/ValidateCpfCnpj';
import AccountSecurity from './components/AccountSecurity';
import CartoesCredito from './components/CartoesCredito';
import Consultas from './components/Consultas';
import Creditos from './components/Creditos';
import Financeiro from './components/Financeiro/Financeiro';
import MeusDados from './components/MeusDados';
import Produtos from './components/Produtos';
import Tabs from './components/Tabs';
import Utilizacao from './components/Utilizacao';
import { config } from '../../../config/envs';
import '../../components/jds/jds.css';
import './clienteDados.css';

export function ClienteDados(props) {
  const dispatch = useDispatch();
  // true no 1º render: o overlay já cobre o form vazio antes do loadClient começar.
  const [loading, setLoading] = useState(true);
  const person_types = useSelector((state) => state.app.person_types);

  const [tab, setTab] = useState('profile_info');
  const [userData, setUserData] = useState(null);

  const subheader = useSubheader();
  useEffect(() => {
    subheader.setBreadcrumbs([
      {
        text: 'Visualizando cliente',
        title: 'Visualizando cliente',
        href: '',
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validaDocument = (document_number) => {
    if (!document_number || typeof document_number !== 'string') {
      return false;
    }

    const documentNumber = document_number.replace(/\D/g, '');

    return validCpf(documentNumber) || validCnpj(documentNumber);
  };

  const [initialValues, setInitialValues] = useState({
    user_id: null,
    oab: '',
    name: '',
    email: '',
    address: '',
    address_number: '',
    address_complement: '',
    phone: '',
    city: null,
    state: null,
    oab_state: null,
    profession: null,
    document_type: null,
    client_type: 'b2c',
    document_number: '',
    postal_code: '',
    district: '',
    manage_url: '',
    password: '',
    confirmation_password: '',
    cards: [],
    comments: '',
    deleted_at: null,
    deleted_by: null,
    delete_reason: '',
    created_by: null,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: (values, { setSubmitting }) => {
      const sanitizedValues = {
        ...values,
        name: values.name?.trim(),
      };

      dispatch({
        type: 'SAVE_PROFILE',
        payload: {
          values: { ...sanitizedValues },
          client_id: props.match.params.cliente_id,
          callback: () => {
            setSubmitting(false);
            loadClient();
            formik.setFieldValue('password', '');
            formik.setFieldValue('confirmation_password', '');
          },
        },
      });
    },
    validationSchema: yup.object().shape({
      name: yup
        .string()
        .trim()
        .required('O campo nome é obrigatório!'),
      oab: yup.string().when('profession', {
        is: (value) =>
          JSON.stringify(value) === JSON.stringify({ id: 1, name: 'Advogado' }),
        then: yup.string().nullable(),
      }),
      phone: yup
        .string()
        .matches(
          /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3}\-[0-9]{4}$/,
          'Telefone inválido.',
        )
        .required('O campo telefone é obrigatório!'),
      city: yup.object().required('O campo cidade é obrigatório!'),
      state: yup.object().required('O campo estado é obrigatório!'),
      address: yup.string().required('O campo endereço é obrigatório!'),
      address_number: yup.string().required('O campo número é obrigatório!'),
      address_complement: yup.string().nullable(),
      postal_code: yup.string().required('O campo CEP é obrigatório!'),
      district: yup.string().required('O campo bairro é obrigatório!'),
      oab_state: yup
        .object()
        .nullable()
        .when('profession', {
          is: (value) =>
            JSON.stringify(value) ===
            JSON.stringify({ id: 1, name: 'Advogado' }),
          then: yup.object().nullable(),
        }),
      profession: yup.object().required('O campo profissão é obrigatório!'),
      document_type: yup.object().required('O campo tipo é obrigatório!'),
      client_type: yup
        .string()
        .oneOf(['b2c', 'b2b'], 'Segmento inválido.')
        .required('O campo segmento é obrigatório!'),
      document_number: yup
        .string()
        .test('document_number', 'CPF/CNPJ inválido.', function(value) {
          return validaDocument(value);
        })
        .required('O campo documento é obrigatório!'),
      confirmation_password: yup
        .string()
        .oneOf([yup.ref('password'), null], 'As senhas não coincidem!')
        .when('password', {
          is: (value) => typeof value === 'string' && value !== '',
          then: yup.string().required('Digite a confirmação da senha!'),
        }),
    }),
  });

  async function loadClient() {
    setLoading(true);

    const response = await axios.get(
      config.app.adminBaseUrl + `/clients/${props.match.params.cliente_id}`,
    );
    const user = response.data;
    let document_type =
      user.document_number.replace(/\D/g, '').length === 11 ? 'pf' : 'pj';
    document_type = person_types.find((type) => type.id === document_type);

    setInitialValues({
      user_id: user.user_id,
      name: user.name,
      email: user.email,
      document_number: user.document_number || '',
      phone: user.phone,
      profession: user._profession_data,
      oab: user.oab,
      address: user.address,
      address_number: user.address_number,
      address_complement: user.address_complement,
      state: user._state_data,
      document_type: document_type,
      client_type: user.client_type || 'b2c',
      city: user._city_data,
      postal_code: user.postal_code,
      district: user.district,
      oab_state: user._oab_state_data,
      manage_url: user.manage_url,
      password: '',
      confirmation_password: '',
      cards: user.cards,
      credits_vehicles: user.credits_vehicles,
      credits_vehicle_plates: user.credits_vehicle_plates,
      credits_personal_info: user.credits_personal_info,
      credits_fgts: user.credits_fgts,
      comments: user.comments,
      deleted_at: user.deleted_at,
      deleted_by: user.deleted_by,
      deleted_reason: user.deleted_reason,
      created_by: user.created_by,
    });

    setLoading(false);
    setUserData(user);
  }

  useEffect(() => {
    dispatch({ type: 'LOAD_STATES' });
    dispatch({ type: 'LOAD_PROFESSIONS' });

    loadClient();
  }, []);

  return (
    <BlockUi tag="div" blocking={loading}>
      <div className="jds-scope">
        <div className="jds-card">
          <Tabs tab={tab} setTab={setTab} />
          {/* key: cada aba entra com fade+slide ao ser escolhida */}
          <div key={tab} className="jds-enter">
            {tab === 'profile_info' && (
              <MeusDados id={props.match.params.cliente_id} formik={formik} />
            )}
            {tab === 'account_security' && (
              <AccountSecurity clientId={props.match.params.cliente_id} />
            )}
            {tab === 'financial' && (
              <Financeiro
                formik={formik}
                cliente_id={props.match.params.cliente_id}
                isBlocked={initialValues.isBlocked}
              />
            )}
            {tab === 'queries' && (
              <Consultas cliente_id={props.match.params.cliente_id} />
            )}
            {tab === 'features' && (
              <Utilizacao
                formik={formik}
                cliente_id={props.match.params.cliente_id}
              />
            )}
            {tab === 'credit_cards' && (
              <CartoesCredito
                formik={formik}
                cliente_id={props.match.params.cliente_id}
              />
            )}
            {tab === 'credits' && (
              <Creditos
                loadClient={loadClient}
                formik={formik}
                cliente_id={props.match.params.cliente_id}
              />
            )}
            {tab === 'products' && (
              <Produtos
                cliente_id={props.match.params.cliente_id}
                user_id={userData?.user_id}
              />
            )}
          </div>
        </div>
      </div>
    </BlockUi>
  );
}
